/**
 * Performance Filter Bar Component
 *
 * Filter controls for the Performance Scorecard tab.
 * Extracted from PerformanceTab.tsx to maintain 500-line limit.
 */
import { Search, X, Loader2 } from 'lucide-react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { Card, CardContent } from '../ui/card'
import SearchableSelect from '../ui/SearchableSelect'
import { type SupplierOption } from './performanceConstants'

interface PerformanceFilterBarProps {
  suppliers: SupplierOption[]
  loadingSuppliers: boolean
  selectedSupplier: string
  onSupplierChange: (value: string) => void
  startDate: string
  onStartDateChange: (value: string) => void
  endDate: string
  onEndDateChange: (value: string) => void
  strategicOnly: boolean
  onStrategicOnlyChange: (value: boolean) => void
  /** Runs the search. Takes an optional supplier override so a dropdown
   *  selection can search immediately, before parent state has re-rendered. */
  onSearch: (supplierOverride?: string) => void
  onCancel: () => void
  /** Supplier portal: the axis flips to "which customer", and "all" is not a
   *  valid option — the scorecard endpoint needs exactly one customer_id. */
  isSupplier?: boolean
  customers?: SupplierOption[]
  selectedCustomer?: string
  onCustomerChange?: (value: string) => void
  loadingCustomers?: boolean
}

export function PerformanceFilterBar({
  suppliers,
  loadingSuppliers,
  selectedSupplier,
  onSupplierChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  strategicOnly,
  onStrategicOnlyChange,
  onSearch,
  onCancel,
  isSupplier = false,
  customers = [],
  selectedCustomer = '',
  onCustomerChange,
  loadingCustomers = false,
}: PerformanceFilterBarProps) {
  return (
    <Card className="shadow-sm">
      <CardContent className="py-4">
        <div className="flex flex-wrap items-end gap-4">
          {/* Customer / Supplier Dropdown — a supplier only ever reports on
              itself, so the useful axis is which customer it is reporting to.
              No "all" entry on the supplier side: the endpoint resolves one
              customer at a time.
              Both pickers are typeable and apply on selection: with hundreds of
              suppliers you had to scroll to find one, then click Search as a
              second step. */}
          {isSupplier ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="customer" className="text-sm font-medium">
                Customer:
              </Label>
              <div className="w-[320px]">
                {loadingCustomers ? (
                  <div className="flex h-10 items-center justify-center rounded-md border">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  <SearchableSelect
                    options={customers.map((customer) => ({
                      value: String(customer.id),
                      label: customer.name,
                    }))}
                    value={selectedCustomer}
                    /* Clearing emits '' — ignored, as there is no unscoped
                       scorecard to fall back to. Selecting re-scopes the fetch
                       on its own, so no onSearch here. */
                    onChange={(val) => { if (val) onCustomerChange?.(val) }}
                    placeholder="Select a customer"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="supplier" className="text-sm font-medium">
                Supplier:
              </Label>
              <div className="w-[320px]">
                {loadingSuppliers ? (
                  <div className="flex h-10 items-center justify-center rounded-md border">
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  <SearchableSelect
                    options={[
                      { value: 'all', label: 'All Suppliers' },
                      ...suppliers.map((supplier) => ({
                        value: String(supplier.id),
                        label: supplier.name,
                      })),
                    ]}
                    value={selectedSupplier}
                    onChange={(val) => {
                      const next = val || 'all'
                      onSupplierChange(next)
                      onSearch(next)
                    }}
                    placeholder="All Suppliers"
                  />
                )}
              </div>
            </div>
          )}

          {/* Start Date */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="startDate" className="text-sm font-medium">
              Start Date:
            </Label>
            <Input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-[140px] h-10"
            />
          </div>

          {/* End Date */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="endDate" className="text-sm font-medium">
              End Date:
            </Label>
            <Input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="w-[140px] h-10"
            />
          </div>

          {/* Strategic Checkbox — narrows a customer's own supplier base, so
              there is nothing for it to filter on the supplier side. */}
          {!isSupplier && (
            <div className="flex items-center gap-2 h-10">
              <Checkbox
                id="strategicOnly"
                checked={strategicOnly}
                onCheckedChange={(checked) => onStrategicOnlyChange(checked === true)}
              />
              <Label htmlFor="strategicOnly" className="text-sm cursor-pointer whitespace-nowrap">
                Strategic suppliers only
              </Label>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-2 h-10">
            <Button onClick={() => onSearch()} className="h-10 bg-blue-500 hover:bg-blue-600">
              <Search className="h-4 w-4 mr-1.5" />
              Search
            </Button>
            <Button variant="outline" className="h-10" onClick={onCancel}>
              <X className="h-4 w-4 mr-1.5" />
              Cancel
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
